/**
 * Who is signed in (Firebase and the Grow account) and the sign-in actions, from AuthContext.
 *
 * A hook rather than useContext at each call. Unlike the other context
 * hooks it cannot tell when it is outside its provider: AuthContext has a
 * default value (nobody signed in, loading), so the null check below never
 * fires and a component outside AuthProvider reads that default. Every
 * route is inside AuthProvider (App), so it does not arise.
 *
 * Who uses it: nearly every screen that needs the account (mysqlUser, its
 * user_id and postal_code) or the sign-in actions.
 *
 * @module useAuth
 */

import { useContext } from 'react';
import { AuthContext, type AuthContextType } from '../context/AuthContextDefinition';

/**
 * The AuthProvider's value.
 *
 * @returns Who is signed in and the sign-in actions.
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
